import { normalizeError } from '../../../utils';
import { StartVideoError, StartVideoErrorCode } from '../../core/types';
import {
  AUDIO_CAPTURE_FAILED_SUBCODE_MESSAGE,
  AudioCaptureFailedSubCode,
  ExecutedFailure,
  ZoomErrorCode,
} from '../types';

function formatErrorMessage(failure: ExecutedFailure): string {
  return `${failure.type} | ${failure.errorCode} | ${failure.reason}`;
}

export class ZoomExecutedFailureError extends Error implements ExecutedFailure {
  type;
  errorCode;
  reason;

  constructor(failure: ExecutedFailure, message?: string) {
    super(message ?? `ZoomExecutedFailureError: ${formatErrorMessage(failure)}`);
    this.type = failure.type;
    this.errorCode = failure.errorCode;
    this.reason = failure.reason;
  }
}

export type AudioCaptureFailure = ExecutedFailure & { subCode: number };

/**
 * Specialized failure for `AUDIO_CAPTURE_FAILED` rejections, the only Zoom failure that carries a `subCode`.
 */
export class AudioCaptureFailedError extends ZoomExecutedFailureError {
  declare errorCode: (typeof ZoomErrorCode)['AUDIO_CAPTURE_FAILED'];
  subCode: AudioCaptureFailedSubCode;

  constructor(failure: AudioCaptureFailure) {
    const subCodeDescription = AUDIO_CAPTURE_FAILED_SUBCODE_MESSAGE[failure.subCode as AudioCaptureFailedSubCode];
    const description = `AudioCaptureFailedError: ${formatErrorMessage(failure)} | ${subCodeDescription}`;
    super(failure, description);

    this.subCode = failure.subCode;
  }
}

/**
 * The `type` Zoom uses for a camera held by another program. It is not part of
 * the SDK's `ErrorTypes` union even though the SDK sets it as a failure `type`.
 */
const CAMERA_TAKEN_FAILURE_TYPE = 'VIDEO_CAMERA_IS_TAKEN';

/**
 * The `DOMException` name browsers use when `getUserMedia` cannot open a device
 * that another process is holding.
 */
const NOT_READABLE_ERROR_NAME = 'NotReadableError';

/**
 * ExecutedFailure is only a type definition.
 * "reason" is a required parameter, but is optional in some return values.
 */
export function isZoomExecutedFailure(error: unknown): error is ExecutedFailure {
  return typeof error === 'object' && error !== null && 'type' in error && 'errorCode' in error;
}

/**
 * Indicates that starting the camera failed because another application is
 * already using it. Zoom surfaces this in three different shapes:
 * 1. in-session `stream.startVideo` rejects with `errorCode` `VIDEO_CAMERA_TAKEN`
 * 2. preview with a virtual background rejects with only the failure `type` and
 *    no `errorCode`, so {@link isZoomExecutedFailure} does not match it
 * 3. preview without a virtual background rejects with the raw `getUserMedia`
 *    `NotReadableError`
 */
export function isCameraTakenError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) {
    return false;
  }

  if ('errorCode' in error && error.errorCode === ZoomErrorCode.VIDEO_CAMERA_TAKEN) {
    return true;
  }

  if ('type' in error && error.type === CAMERA_TAKEN_FAILURE_TYPE) {
    return true;
  }

  return 'name' in error && error.name === NOT_READABLE_ERROR_NAME;
}

/**
 * Translates a Zoom rejection into a vendor-neutral {@link StartVideoError}.
 * Detection has to happen here, on the raw rejection, because
 * {@link normalizeError} flattens the shapes without an `errorCode` into an
 * opaque JSON string.
 */
export function toStartVideoError(error: unknown): StartVideoError {
  const code = isCameraTakenError(error) ? StartVideoErrorCode.CAMERA_TAKEN : StartVideoErrorCode.UNKNOWN;

  // the raw rejection is the cause, since normalizing it away would drop the
  // stack of anything that already was an Error
  return new StartVideoError(code, normalizeError(error).message, error);
}

function isAudioCaptureFailure(failure: ExecutedFailure): failure is AudioCaptureFailure {
  return failure.errorCode === ZoomErrorCode.AUDIO_CAPTURE_FAILED && failure.subCode !== undefined;
}

export function toZoomExecutedFailureError(failure: ExecutedFailure): ZoomExecutedFailureError {
  return isAudioCaptureFailure(failure) ? new AudioCaptureFailedError(failure) : new ZoomExecutedFailureError(failure);
}

/**
 * Returns a Zoom error code and {@link ExecutedFailure} tuple if the error is a
 * Zoom failure, otherwise returns a normalized error.
 */
export function toZoomError(error: unknown): [number, ExecutedFailure] | [null, Error] {
  if (isZoomExecutedFailure(error)) {
    return [error.errorCode, error];
  }

  return [null, normalizeError(error)];
}
